<template>
  <div
    ref="root"
    class="flex flex-col gap-3 rounded-lg border border-violet-300 bg-white p-4"
    role="group"
    :aria-label="`Answer the delivery request for ${poNumber}`"
    @keydown="onKeydown"
  >
    <div class="flex flex-col gap-0.5">
      <b class="text-sm text-gray-900">Answer: what can you ship by {{ fmtDay(request.shipBy) }}?</b>
      <span class="text-xs text-gray-500">Pieces to plan: tick the lines for each shipment. Add a shipment for anything that ships later.</span>
    </div>

    <section v-for="(card, i) in cards" :key="card.key" class="flex flex-col gap-2 border-t border-gray-200 pt-3" :aria-label="`Shipment ${i + 1}`">
      <header class="flex flex-wrap items-center gap-3">
        <b class="text-sm text-gray-900">Shipment {{ i + 1 }}</b>
        <label class="flex items-center gap-2 text-sm text-gray-600">
          Ship date
          <input
            v-model="card.plannedDate"
            type="date"
            class="input h-8 !w-auto"
            :class="error?.field === `card-${i}-date` ? 'border-red-400' : ''"
            :data-field="`card-${i}-date`"
            :aria-invalid="error?.field === `card-${i}-date`"
            :aria-describedby="describedBy(`card-${i}-date`)"
          />
        </label>
        <span v-if="card.plannedDate" class="text-xs font-semibold" :class="card.plannedDate > request.shipBy ? 'text-red-700' : 'text-green-700'">
          {{ vsRequestedText(card.plannedDate, request.shipBy) }}
        </span>
        <span v-if="error?.field === `card-${i}-date`" :id="errorId(`card-${i}-date`)" class="text-xs text-red-700">{{ error.message }}</span>
        <button v-if="cards.length > 1" type="button" :class="REMOVE_BUTTON" :aria-label="`Remove shipment ${i + 1}`" @click="removeCard(i)">×</button>
      </header>
      <LineTable v-model="card.lines" :lines="lines" :left="leftForCard(lines, cards, i)" :error-field="lineErrorField(i, card.lines)" :error-id="errorId('table')" />
    </section>

    <div class="flex flex-wrap items-center gap-4">
      <button type="button" :class="DASHED_BUTTON" @click="addCard">+ Add another shipment</button>
      <span class="text-sm text-gray-600">
        <template v-if="rest.lines > 0">
          <b class="tabular-nums">{{ fmtPcs(rest.pcs) }}</b>
          pcs ({{ linesLabel(rest.lines) }}) on no shipment: they stay without a date.
        </template>
        <template v-else>Every piece is on a shipment.</template>
      </span>
    </div>
    <p v-if="tableError" :id="errorId('table')" class="m-0 text-sm text-red-700" role="alert">{{ tableError }}</p>

    <div class="flex flex-col gap-1">
      <label class="label flex flex-col gap-1">
        <span>
          Note to viaPhoton
          <span v-if="!later" class="font-normal text-gray-500">(optional)</span>
        </span>
        <input
          v-model="note"
          class="input h-9"
          :class="error?.field === 'note' ? 'border-red-400' : ''"
          data-field="note"
          :placeholder="later ? `Why can part of it not ship by ${fmtDate(request.shipBy)}?` : 'Optional'"
          :aria-invalid="error?.field === 'note'"
          :aria-describedby="describedBy('note')"
        />
      </label>
      <span v-if="error?.field === 'note'" :id="errorId('note')" class="text-xs text-red-700">{{ error.message }}</span>
    </div>
    <p v-if="hasPlanned" class="m-0 text-xs text-gray-500">Shipments already planned stay as they are.</p>
    <p v-if="serverError" class="m-0 text-sm text-red-700" role="alert">{{ serverError }}</p>

    <div class="flex flex-wrap items-center justify-end gap-3">
      <button type="button" class="btn-secondary" @click="emit('close')">Cancel</button>
      <button type="button" class="btn-primary" :disabled="sending" @click="send">{{ sending ? 'Sending…' : 'Send answer' }}</button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, ref } from 'vue';

  import LineTable from '@/components/poTracker/LineTable.vue';
  import { DASHED_BUTTON, REMOVE_BUTTON } from '@/components/poTracker/poTrackerStyles';
  import { useFieldError } from '@/composables/useFieldError';
  import logger from '@/services/logger';
  import { answerRequest } from '@/services/poTrackerService';
  import { usePoTrackerStore } from '@/store/poTrackerStore';
  import { SHIPMENT_STATUS, type DeliveryRequest, type PoLineQuantities, type Shipment } from '@/types/poTracker';
  import { extractErrorMessage } from '@/utils/apiError';
  import {
    answerBody,
    fmtDate,
    fmtDay,
    fmtPcs,
    leftForCard,
    linesLabel,
    piecesOnNoShipment,
    validateAnswer,
    vsRequestedText,
    type FieldError,
    type LineDraft,
  } from '@/utils/poTracker';

  const HTTP_CONFLICT = 409;

  interface Card {
    key: number;
    plannedDate: string;
    lines: LineDraft[];
  }

  const props = defineProps<{ poNumber: string; request: DeliveryRequest; lines: PoLineQuantities[]; shipments: Shipment[] }>();
  const emit = defineEmits<{ close: [] }>();

  const store = usePoTrackerStore();
  const root = ref<HTMLElement | null>(null);

  let nextKey = 0;
  const newCard = (plannedDate: string): Card => ({ key: nextKey++, plannedDate, lines: [] });

  const cards = ref<Card[]>([newCard(props.request.shipBy)]);
  const note = ref('');
  const error = ref<FieldError | null>(null);
  const serverError = ref<string | null>(null);
  const sending = ref(false);
  const { errorId, describedBy } = useFieldError(error);

  const rest = computed(() => piecesOnNoShipment(props.lines, cards.value));
  const later = computed(() => cards.value.some((c) => c.plannedDate > props.request.shipBy));
  const hasPlanned = computed(() => props.shipments.some((s) => s.status === SHIPMENT_STATUS.PLANNED));
  // A quantity error isn't tied to one card: it reads once below the cards and marks the line in each card.
  const tableError = computed(() => (error.value && /^(card-\d+-lines|qty-)/.test(error.value.field) ? error.value.message : null));

  function lineErrorField(index: number, drafts: LineDraft[]): string | null {
    const field = error.value?.field ?? '';
    if (field === `card-${index}-lines`) return 'lines';
    if (field.startsWith('qty-') && drafts.some((d) => `qty-${d.odooLineId}` === field)) return field;
    return null;
  }

  onMounted(() => focusField('card-0-date'));

  function focusField(field: string): void {
    void nextTick(() => root.value?.querySelector<HTMLElement>(`[data-field="${field}"]`)?.focus());
  }

  function addCard(): void {
    cards.value = [...cards.value, newCard('')];
  }

  function removeCard(index: number): void {
    cards.value = cards.value.filter((_, i) => i !== index);
    error.value = null;
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      emit('close');
      return;
    }
    // Enter on a button, a checkbox or the line filter keeps its own meaning.
    const target = event.target as HTMLElement;
    if (event.key === 'Enter' && !target.hasAttribute('data-no-submit') && target.tagName === 'INPUT' && (target as HTMLInputElement).type !== 'checkbox') {
      event.preventDefault();
      void send();
    }
  }

  async function send(): Promise<void> {
    if (sending.value) return;
    serverError.value = null;
    error.value = validateAnswer({
      cards: cards.value,
      left: new Map(props.lines.map((l) => [l.odooLineId, l.toPlan])),
      shipBy: props.request.shipBy,
      note: note.value,
    });
    if (error.value) {
      if (!error.value.field.endsWith('-lines')) focusField(error.value.field);
      return;
    }
    sending.value = true;
    try {
      const answered = await answerRequest(props.request.id, answerBody(cards.value, note.value));
      const planned = answered.answer?.length ?? cards.value.length;
      logger.showSuccess(`Answer sent · ${planned === 1 ? '1 shipment' : `${planned} shipments`} planned`);
      emit('close');
      await store.afterWrite([props.poNumber]);
    } catch (sendError) {
      // 409: the request was answered or withdrawn meanwhile, so the form no longer applies.
      if ((sendError as { response?: { status?: number } } | null)?.response?.status === HTTP_CONFLICT) {
        logger.showError(extractErrorMessage(sendError));
        emit('close');
        await store.afterWrite([props.poNumber]);
        return;
      }
      serverError.value = extractErrorMessage(sendError);
    } finally {
      sending.value = false;
    }
  }
</script>
